import React, { createContext } from "react";

import { Subject } from "rxjs";

import { DisplayMode } from "../../layers/components/layers-tab/constants/layersTab.constants";
import {
  ProjectContextState,
  UpdateCheckedLayerKeys,
  UpdateCheckedTreeKeys,
  UpdateDeleteModal,
  UpdateDisplaySettings,
  UpdateErrorState,
  UpdateIsAddingExistingFolders,
  UpdateIsHidingUnselectedLayers,
  UpdateIsShowOnlySelectedButtonEnabled,
  UpdateIsTreeDraggable,
  UpdateIsUpdatingShapefile,
  UpdateIsUploadingShapefileList,
  UpdateLayerTreeExpandedKeys,
  UpdateLoadingState,
  UpdateManageMode,
  UpdateManagementTree,
  UpdateNewlyCreatedFolderId,
  UpdateProjectPermissions,
  UpdateProjectState,
  UpdateSearchText,
  UpdateSelectedGroups,
  UpdateSelectedProjectId,
  UpdateSelectedView,
  UpdateUploadProgress
} from "./ProjectContextState";

// The context objects and their initial state live in this leaf module, apart
// from ProjectProvider (ProjectContext.tsx): the provider imports
// ProjectWrapper and through it a large slice of the app, so consumers that
// only need useProjectContext/useProjectDispatch would otherwise drag that
// whole graph into their own module graph (and into every test that imports
// them).

export const initialProjectContextState: ProjectContextState = {
  checkedLayerKeys: [],
  displaySettings: {
    displayMode: DisplayMode.HIERARCHY,
    sortDirection: "asc",
    hideUnselected: false
  },
  error: undefined,
  isAddingExistingFolders: true,
  isHidingUnselectedLayers: false,
  isLoading: false,
  isShowOnlySelectedButtonEnabled: true,
  isTreeDraggable: true,
  isUpdatingShapefile: false,
  isUploadingShapefileList: false,
  uploadProgress: null,
  layerTreeExpandedKeys: [],
  projectRefreshObserver: new Subject(),
  selectedProjectId: undefined,
  selectedViewId: undefined,
  managementTree: {
    selectedTreeNodes: null,
    uploadedRawShapefiles: []
  },
  searchText: "",
  selectedGroups: [],
  newlyCreatedFolderId: null,
  manageMode: {
    isEnabled: false,
    checkedKeys: []
  },
  deleteModal: {
    isVisible: false
  }
};

export const ProjectContext = createContext(initialProjectContextState);

export interface Action<T, Type> {
  type?: Type;
  payload: T;
}

export const ProjectDispatchContext =
  createContext<React.Dispatch<Action<ProjectUpdates, "update">>>(undefined);

export type ProjectUpdates =
  | UpdateCheckedLayerKeys
  | UpdateCheckedTreeKeys
  | UpdateDeleteModal
  | UpdateDisplaySettings
  | UpdateErrorState
  | UpdateIsAddingExistingFolders
  | UpdateIsHidingUnselectedLayers
  | UpdateIsUpdatingShapefile
  | UpdateIsUpdatingShapefile
  | UpdateIsUploadingShapefileList
  | UpdateLayerTreeExpandedKeys
  | UpdateLoadingState
  | UpdateManagementTree
  | UpdateProjectPermissions
  | UpdateProjectState
  | UpdateSearchText
  | UpdateSelectedGroups
  | UpdateSelectedProjectId
  | UpdateSelectedView
  | UpdateIsShowOnlySelectedButtonEnabled
  | UpdateNewlyCreatedFolderId
  | UpdateIsTreeDraggable
  | UpdateManageMode
  | UpdateUploadProgress;
